import * as q from "../../queries.ts";
import type { PostView } from "../../queries.ts";
import { postTitle } from "../../titles.ts";
import { formatError, RpcClient, XrpcError } from "../api.ts";
import { getEagerPost, removeEagerPost } from "../eager-posts.ts";
import { viewTransport } from "../auth.ts";
import { cachedPost, resolveDid } from "../view-cache.ts";
import { PagedList } from "../components/paged-list.tsx";
import { PostCard, PostResultView } from "../components/post.tsx";

export function ThreadView(actor: string, rkey: string, signal: AbortSignal): Element {
  const main = (
    <main class="thread-page">
      <p class="status">Loading thread…</p>
    </main>
  );
  void resolveThread(actor, rkey, main, signal);
  return main;
}

async function resolveThread(
  actor: string,
  rkey: string,
  target: Element,
  signal: AbortSignal,
): Promise<void> {
  let uri: `at://${string}`;
  try {
    const did = await resolveDid(new RpcClient(viewTransport(q.getProfile.id)), actor, signal);
    uri = `at://${did}/blue.cerulea.app.post/${rkey}` as const;
  } catch (error) {
    if (!signal.aborted) target.replaceChildren(threadError(error));
    return;
  }
  if (signal.aborted) return;
  const eager = getEagerPost(uri);
  const known = eager ?? cachedPost(uri);
  if (known) {
    document.title = postTitle(known);
    showFocal(
      known,
      target,
      signal,
      eager
        ? "This post was just published — showing your local copy until the AppView catches up."
        : "Loading thread…",
    );
  }
  await loadThread(uri, target, signal, eager);
}

function threadError(error: unknown): Element {
  return (
    <div class="error" role="alert">
      <strong>Could not load this thread</strong>
      <span>{formatError(error)}</span>
    </div>
  );
}

async function loadThread(
  uri: `at://${string}`,
  target: Element,
  signal: AbortSignal,
  eager: PostView | undefined,
): Promise<void> {
  const client = new RpcClient(viewTransport(q.getThread.id));
  let page: Awaited<ReturnType<typeof pollThread>>;
  try {
    page = await pollThread(client, uri, signal, eager);
  } catch (error) {
    if (signal.aborted) return;
    // A missing post with a local copy just hasn't been indexed yet; keep showing it.
    if (eager && error instanceof XrpcError && error.error === "PostNotFound") return;
    target.replaceChildren(threadError(error));
    return;
  }
  if (signal.aborted) return;
  document.title = postTitle(page.post);

  // Strong references resolve by URI; a CID drift means the referenced post was edited since.
  const cids = new Map<string, string>();
  for (const result of [...page.ancestors, ...page.replies]) {
    if (result.$type === "blue.cerulea.app.views#availablePost") {
      cids.set(result.post.uri, result.post.cid);
    }
  }
  cids.set(page.post.uri, page.post.cid);
  const notice = (post: PostView): string | undefined => {
    const reply = post.record.reply;
    if (!reply) return undefined;
    const drifted = (["parent", "root"] as const).filter((ref) => {
      const current = cids.get(reply[ref].uri);
      return current !== undefined && current !== reply[ref].cid;
    });
    if (!drifted.length) return undefined;
    // A direct reply to the root has parent === root; don't report it twice.
    const subject = reply.parent.uri === reply.root.uri || drifted.length === 1
      ? `${drifted[0]} post was`
      : "parent and root posts were";
    return `The ${subject} edited after this reply was made`;
  };
  const row = (result: q.PostResult, variant: "thread" | "focal") =>
    PostResultView({
      result,
      signal,
      variant,
      notice: result.$type === "blue.cerulea.app.views#availablePost"
        ? notice(result.post)
        : undefined,
    });

  const ancestors = <section class="thread-ancestors" aria-label="Earlier posts"></section>;
  if (page.ancestorsTruncated) {
    ancestors.append(<p class="status">Some earlier posts are not shown.</p>);
  }
  for (const result of page.ancestors) {
    const rendered = row(result, "thread");
    if (rendered) ancestors.append(rendered);
  }

  const focal = <section class="thread-focal" aria-label="Selected post"></section>;
  focal.append(
    PostCard({ post: page.post, signal, variant: "focal", notice: notice(page.post) }),
  );

  // The first page of replies arrived with the thread; later pages fetch replies only.
  let first: typeof page | undefined = page;
  const replies = PagedList(
    async (cursor) => {
      const loaded = first ?? await client.call(q.getThread, {
        parameters: { uri, limit: 30, ancestorLimit: 0, ...(cursor && { cursor }) },
        signal,
      });
      first = undefined;
      return { items: loaded.replies, ...(loaded.cursor && { cursor: loaded.cursor }) };
    },
    (result) =>
      result.$type === "blue.cerulea.app.views#availablePost" ? result.post.uri : result.uri,
    (result) => row(result, "thread"),
    signal,
    "No visible replies.",
  );
  replies.setAttribute("aria-label", "Replies");
  const shown = target.querySelector(".thread-focal")?.getBoundingClientRect().top;
  target.replaceChildren(ancestors, focal, replies);
  // Ancestors land above a focal post that was already on screen; keep it where the reader saw it.
  if (shown !== undefined) scrollBy(0, focal.getBoundingClientRect().top - shown);
  if (eager) removeEagerPost(uri);
}

async function pollThread(
  client: RpcClient,
  uri: `at://${string}`,
  signal: AbortSignal,
  eager: PostView | undefined,
) {
  for (let attempt = 0;; attempt++) {
    try {
      return await client.call(q.getThread, {
        parameters: { uri, limit: 30, ancestorLimit: 20 },
        signal,
      });
    } catch (error) {
      const missing = error instanceof XrpcError && error.error === "PostNotFound";
      if (!missing || !eager || attempt >= 10 || signal.aborted) throw error;
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
  }
}

/** The focal post alone, from a view already in hand, while the thread around it loads. */
function showFocal(post: PostView, target: Element, signal: AbortSignal, status: string): void {
  const focal = <section class="thread-focal" aria-label="Selected post"></section>;
  focal.append(PostCard({ post, signal, variant: "focal" }));
  target.replaceChildren(
    <section class="thread-ancestors" aria-label="Earlier posts"></section>,
    focal,
    <p class="status">{status}</p>,
  );
}
